import React, { useContext, useEffect } from "react";
import {
    ContentSection,
    ButtonProps,
    ButtonRenderProps,
    createListTableColumns,
    ListTable,
    renderBooleanIcon,
    Stack,
    Text
} from "sprint-common-web";

import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { ciRoutes, getDeliveryBucketHref } from "../../ContentIngestionRoutes";
import { ChangeStateDeliveryBucketButton } from "../change-state/ChangeStateDeliveryBucketButton";
import { DeliveryBucketListingContext } from "../context/DeliveryBucketListingContext";
import { AddDeliveryBucketButton } from "../create/AddDeliveryBucketButton";
import { DeliveryBucketAccessType } from "../create/common/DeliveryBucketAccessType";
import { DeleteDeliveryBucketButton } from "../delete/DeleteDeliveryBucketButton";
import { InviteToDeliveryBucketButton } from "../invite/InviteToDeliveryBucketButton";
import { DeliveryBucketItem } from "./DeliveryBucketItem";
import { useDeliveryBucketListing } from "./useDeliveryBucketListing";
import { useDeliveryBucketListingFilters } from "./useDeliveryBucketListingFilters";

export const DeliveryBucketListing: React.VFC = () => {
    const { filters, setSort, setPage, setPageSize } = useDeliveryBucketListingFilters();
    const { items, totalItems, saveData } = useDeliveryBucketListing();

    const { deliveryBucketItems, isLoading } = useContext(DeliveryBucketListingContext);

    useEffect(() => {
        saveData(deliveryBucketItems, {
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder
        });
    }, [
        filters.page,
        filters.pageSize,
        filters.sortBy,
        filters.sortOrder,
        deliveryBucketItems,
        deliveryBucketItems.length
    ]);

    const columns = createListTableColumns<DeliveryBucketItem>([
        {
            type: "link",
            key: "uniqueId",
            name: "Unique ID",
            minWidth: 160,
            maxWidth: 160,
            isSortable: true,
            getHref: (item) => getDeliveryBucketHref(item.deliveryBucketId, item.bucketName, item.uniqueId)
        },
        {
            type: "basic",
            key: "originator",
            name: "Originator",
            minWidth: 160,
            maxWidth: 160
        },
        {
            type: "basic",
            key: "isActive",
            name: "Active",
            minWidth: 45,
            maxWidth: 45,
            onRender: renderBooleanIcon
        },
        {
            type: "basic",
            key: "resources",
            name: "Details",
            minWidth: 350,
            maxWidth: 400,
            onRender: renderDeliveryBucketDetails
        },
        {
            type: "basic",
            key: "accessType",
            name: "Access Type",
            minWidth: 80,
            maxWidth: 120,
            onRender: renderAccessType
        },
        {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 64,
            maxWidth: 64,
            buttons: [
                {
                    id: "delete",
                    render: DeleteDeliveryBucketButton,
                    getItemId: (item) => item.deliveryBucketId
                },
                {
                    id: "change_state",
                    render: ChangeStateDeliveryBucketButton,
                    getItemId: (item) => item.deliveryBucketId
                },
                {
                    id: "invite",
                    render: InviteToDeliveryBucketButton,
                    getItemId: (item) => item.deliveryBucketId
                }
            ]
        }
    ]);

    const addDeliveryBucketButton: ButtonRenderProps<ButtonProps> = {
        render: AddDeliveryBucketButton
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={ciRoutes} />
            </PageHeader>
            <ContentSection header="Buckets" buttons={[addDeliveryBucketButton]}>
                <ListTable
                    columns={columns}
                    items={items}
                    pagination={{
                        page: filters.page,
                        pageSize: filters.pageSize,
                        totalItems,
                        onPageChange: setPage,
                        onPageSizeChange: setPageSize
                    }}
                    sort={{ by: filters.sortBy, order: filters.sortOrder }}
                    onSortChange={setSort}
                    isLoading={isLoading}
                />
            </ContentSection>
        </>
    );
};

const renderAccessType = (item: DeliveryBucketItem) => {
    return <>{DeliveryBucketAccessType[item.accessType]}</>;
};

const renderDeliveryBucketDetails = (item: DeliveryBucketItem) => {
    switch (item.accessType) {
        case DeliveryBucketAccessType.Key: {
            return (
                <Stack tokens={{ childrenGap: 15 }}>
                    <Stack>
                        <Text variant="smallPlus" value="Bucket name" />
                        <Text variant="smallPlus" value={item.bucketName} styles={{ root: { fontWeight: "bold" } }} />
                    </Stack>
                    <Stack>
                        <Text variant="smallPlus" value="Key age (days)" />
                        <Text
                            variant="smallPlus"
                            value={item.resources.keyAge}
                            styles={{ root: { fontWeight: "bold" } }}
                        />
                    </Stack>
                </Stack>
            );
        }
        case DeliveryBucketAccessType.Role: {
            return (
                <Stack tokens={{ childrenGap: 15 }}>
                    <Stack>
                        <Text variant="smallPlus" value="Bucket name" />
                        <Text variant="smallPlus" value={item?.bucketName} styles={{ root: { fontWeight: "bold" } }} />
                    </Stack>
                    <Stack>
                        <Text variant="smallPlus" value="Role arn" />
                        <Text
                            variant="smallPlus"
                            value={item?.resources.roleArn}
                            styles={{ root: { fontWeight: "bold" } }}
                        />
                    </Stack>
                </Stack>
            );
        }
        default:
            throw new Error("Incorrect access type for delivery bucket");
    }
};
